import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import StationStatusConfirmDialog from "./StationStatusConfirmDialog";
import { getApiErrorMessage } from "../../../utils/apiError";
import { colors } from "../../../theme/colors";

interface Station {
  id: string;
  stationId: string;
  name: string;
  stationTypeValue: string;
  type: "Fuel Station" | "Testing Centre";
  status: "Active" | "Inactive";
  managerId: string;
  manager: string;
  city: string;
  state: string;
  branchName: string;
  ownershipType: string;
  address: string;
  pinCode: string;
  pesoApprovalNumber?: string;
}

interface StationManagementProps {
  refreshKey?: number;
  onEditStation?: (station: Station) => void;
}

interface StationsApiResponse {
  count?: number;
  total?: number;
  total_count?: number;
  results?: unknown[];
  items?: unknown[];
  data?: unknown[];
  stations?: unknown[];
}

const PAGE_SIZE = 20;

const stationTypeFilters = [
  { label: "ALL", value: "ALL" },
  { label: "FUEL STATION", value: "Fuel Station" },
  { label: "TESTING CENTRE", value: "Testing Centre" },
];

const statusFilters = [
  { label: "ALL", value: "ALL" },
  { label: "ACTIVE", value: "Active" },
  { label: "INACTIVE", value: "Inactive" },
];

const filterButtonSx = (isSelected: boolean) => ({
  minWidth: "fit-content",
  px: 1.5,
  py: 1.5,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: isSelected ? "#D1D5DB" : "transparent",
  bgcolor: isSelected ? "#F3F4F6" : "transparent",
  color: isSelected ? colors.black : "rgba(0, 0, 0, 0.54)",
  fontSize: "12px",
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  lineHeight: 1,
  boxShadow: "none",
  textTransform: "none",
  "&:hover": {
    borderColor: "#D1D5DB",
    bgcolor: isSelected ? "#F3F4F6" : "#F9FAFB",
    boxShadow: "none",
  },
});

const filterInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#D1D5DB",
    },
    "&:hover fieldset": {
      borderColor: "#C4CBD5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#9CA3AF",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: colors.black,
    fontFamily: "'Inter', sans-serif",
    py: 1.25,
  },
};

const getStationsArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const response = payload as StationsApiResponse;
  return (
    response.results ||
    response.items ||
    response.data ||
    response.stations ||
    []
  );
};

const getStationsTotal = (payload: unknown, fallbackLength: number) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return fallbackLength;
  }

  const response = payload as StationsApiResponse;
  return (
    response.count || response.total || response.total_count || fallbackLength
  );
};

const normalizeStationType = (
  value?: string,
): "Fuel Station" | "Testing Centre" => {
  if (
    value === "TESTING_CENTRE" ||
    value === "TESTING_CENTER" ||
    value === "Testing Centre" ||
    value === "Testing Center"
  ) {
    return "Testing Centre";
  }

  return "Fuel Station";
};

const normalizeStatus = (value: unknown): "Active" | "Inactive" => {
  if (typeof value === "boolean") {
    return value ? "Active" : "Inactive";
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "active" ? "Active" : "Inactive";
  }

  return "Inactive";
};

const normalizeStation = (item: any, index: number): Station => ({
  id: item?.id || item?.station_id || item?.station_code || `ST-${index + 1}`,
  stationId:
    item?.station_id || item?.station_code || item?.id || `ST-${index + 1}`,
  name: item?.name || item?.station_name || item?.title || "Unnamed Station",
  stationTypeValue: item?.station_type || item?.type || "FUEL_STATION",
  type: normalizeStationType(item?.station_type || item?.type),
  status: normalizeStatus(item?.is_active ?? item?.status),
  managerId: item?.manager_id || item?.manager?.id || "",
  manager:
    item?.manager_name || item?.manager?.name || item?.assign_manager || "--",
  city: item?.city || "--",
  state: item?.state || "--",
  branchName: item?.branch_name || item?.branchName || item?.cgd_name || "--",
  ownershipType: item?.ownership_type || item?.ownershipType || "--",
  address: item?.address || item?.station_address || "--",
  pinCode: item?.pincode || item?.pinCode || "--",
  pesoApprovalNumber:
    item?.peso_approval_number || item?.pesoApprovalNumber || "--",
});

const getStationTypeFilterValue = (value: string) => {
  if (value === "Fuel Station") {
    return "FUEL_STATION";
  }

  if (value === "Testing Centre") {
    return "TESTING_CENTER";
  }

  return undefined;
};

const getStationFilterParams = ({
  stationType,
  status,
  city,
  state,
  branchName,
  pinCode,
}: {
  stationType: string;
  status: string;
  city: string;
  state: string;
  branchName: string;
  pinCode: string;
}) => ({
  search: undefined,
  station_type: getStationTypeFilterValue(stationType),
  is_active: status === "ALL" ? undefined : status === "Active",
  city: city.trim() || undefined,
  state: state.trim() || undefined,
  branch_name: branchName.trim() || undefined,
  pincode: pinCode.trim() || undefined,
});

const getStationExportData = (stations: Station[]) =>
  stations.map((station) => ({
    "Station ID": station.stationId,
    "Station Name": station.name,
    Type: station.type,
    Status: station.status,
    Manager: station.manager,
    City: station.city,
    State: station.state,
    "CGD Name": station.branchName,
    Address: station.address,
    "Pin Code": station.pinCode,
  }));

const StationManagement: React.FC<StationManagementProps> = ({
  refreshKey = 0,
  onEditStation,
}) => {
  const navigate = useNavigate();
  const [stationType, setStationType] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [branchName, setBranchName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [pinCode, setPinCode] = useState<string>("");
  const [page, setPage] = useState(1);
  const [stationsData, setStationsData] = useState<Station[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchStations = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get(endpoints.stations.getAll, {
          params: {
            ...getStationFilterParams({
              stationType,
              status,
              city,
              state,
              branchName,
              pinCode,
            }),
            address: address.trim() || undefined,
            page,
            page_size: PAGE_SIZE,
          },
          signal: controller.signal,
        });

        const stations = getStationsArray(response.data).map((item, index) =>
          normalizeStation(item, index),
        );

        setStationsData(stations);
        setTotalCount(getStationsTotal(response.data, stations.length));
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }

        const message = getApiErrorMessage(
          err,
          "Failed to load stations. Please try again.",
        );

        setErrorMessage(message);
        setStationsData([]);
        setTotalCount(0);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchStations();

    return () => {
      controller.abort();
    };
  }, [
    stationType,
    status,
    city,
    state,
    branchName,
    address,
    pinCode,
    page,
    refreshKey,
  ]);

  const handleClearFilters = () => {
    setStationType("ALL");
    setStatus("ALL");
    setCity("");
    setState("");
    setBranchName("");
    setAddress("");
    setPinCode("");
    setPage(1);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    try {
      const response = await api.get(endpoints.stations.export, {
        params: getStationFilterParams({
          stationType,
          status,
          city,
          state,
          branchName,
          pinCode,
        }),
      });

      const stations = getStationsArray(response.data).map((item, index) =>
        normalizeStation(item, index),
      );
      const exportData = getStationExportData(stations);

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns (optional - set column widths)
      const columnWidths = [
        { wch: 12 },
        { wch: 25 },
        { wch: 15 },
        { wch: 10 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 40 },
        { wch: 10 },
      ];
      worksheet["!cols"] = columnWidths;

      // Style the header row
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:J1");
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[address]) continue;
        worksheet[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "3B82F6" } },
          alignment: { horizontal: "center" },
        };
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Stations");

      // Add summary sheet
      const summaryData = [
        ["Report Generated", new Date().toLocaleString()],
        ["Exported Stations", stations.length],
        ["Total Records", getStationsTotal(response.data, stations.length)],
        [
          "Fuel Stations",
          stations.filter((station) => station.type === "Fuel Station").length,
        ],
        [
          "Testing Centres",
          stations.filter((station) => station.type === "Testing Centre")
            .length,
        ],
        [
          "Active Stations",
          stations.filter((station) => station.status === "Active").length,
        ],
        [
          "Inactive Stations",
          stations.filter((station) => station.status === "Inactive").length,
        ],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      // Generate filename with current date
      const fileName = `Station_Report_${new Date().toISOString().split("T")[0]}.xlsx`;

      // Export to Excel
      XLSX.writeFile(workbook, fileName);
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        "Failed to export stations. Please try again.",
      );

      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleToggleStationStatus = async () => {
    if (!selectedStation) {
      return;
    }

    const nextIsActive = selectedStation.status !== "Active";
    setIsStatusUpdating(true);

    try {
      await api.patch(endpoints.stations.updateStatus(selectedStation.id), {
        is_active: nextIsActive,
      });

      setStationsData((prev) =>
        prev.map((station) =>
          station.id === selectedStation.id
            ? { ...station, status: nextIsActive ? "Active" : "Inactive" }
            : station,
        ),
      );
      setSelectedStation(null);
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        "Failed to update station status. Please try again.",
      );

      setErrorMessage(message);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    return status === "Active" ? "#10B981" : "#EF4444";
  };

  const getTypeColor = (type: string) => {
    return type === "Fuel Station" ? "#3B82F6" : "#A855F7";
  };

  const handleOpenStationDashboard = (station: Station) => {
    navigate(`/dashboard/station-onboarding/${station.id}`, {
      state: { station },
    });
  };

  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh", py: 3 }}>
      <Container maxWidth="xl">
        <Paper
          sx={{
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #D1D5DB",
            boxShadow: "0 2px 6px rgba(15, 23, 42, 0.14)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                color: colors.black,
                mb: 3,
              }}
            >
              Filter & Search
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 3, md: 6 },
                flexWrap: "wrap",
                mb: 2.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    mb: 1.25,
                  }}
                >
                  Station Type
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {stationTypeFilters.map((option) => (
                    <Button
                      key={option.value}
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setStationType(option.value);
                        setPage(1);
                      }}
                      sx={filterButtonSx(stationType === option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                    mb: 1.25,
                  }}
                >
                  Status
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {statusFilters.map((option) => (
                    <Button
                      key={option.value}
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setStatus(option.value);
                        setPage(1);
                      }}
                      sx={filterButtonSx(status === option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="City"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setPage(1);
                }}
                sx={filterInputSx}
              />
              <TextField
                fullWidth
                size="small"
                placeholder="State"
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  setPage(1);
                }}
                sx={filterInputSx}
              />
              {/* <TextField
                fullWidth
                size="small"
                placeholder="Branch Name"
                value={branchName}
                onChange={(e) => {
                  setBranchName(e.target.value);
                  setPage(1);
                }}
                sx={filterInputSx}
              />
              <TextField
                fullWidth
                size="small"
                placeholder="Address"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setPage(1);
                }}
                sx={filterInputSx}
              /> */}
              <TextField
                fullWidth
                size="small"
                placeholder="Pin Code"
                value={pinCode}
                onChange={(e) => {
                  setPinCode(e.target.value);
                  setPage(1);
                }}
                sx={filterInputSx}
              />
            </Box>
          </Box>
        </Paper>

        <Paper
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #F1F5F9",
              bgcolor: "white",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Station List
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isExporting}
              sx={{
                textTransform: "none",
                borderColor: colors.primary,
                color: colors.primary,
                px: 2,
                py: 0.9,
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                "&:hover": {
                  borderColor: colors.primary,
                  bgcolor: "#F0FDF4",
                  color: colors.primary,
                },
              }}
            >
              {isExporting ? "Exporting..." : "Export Report"}
            </Button>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F7F7F7" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    Station Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    Manager
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                  >
                    City
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      borderBottom: "1px solid #E7E5E4",
                    }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box
                        sx={{
                          py: 7,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  stationsData.map((station) => (
                    <TableRow
                      key={station.id}
                      onClick={() => handleOpenStationDashboard(station)}
                      hover
                      sx={{
                        cursor: "pointer",
                        "& td": {
                          borderBottom: "1px solid #F1F5F9",
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.textGray,
                            fontWeight: 400,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "13px",
                          }}
                        >
                          {station.stationId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.black,
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "13px",
                          }}
                        >
                          {station.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={station.type}
                          size="small"
                          sx={{
                            bgcolor: `${getTypeColor(station.type)}15`,
                            color: getTypeColor(station.type),
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "11px",
                            borderRadius: "999px",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={station.status}
                          size="small"
                          sx={{
                            bgcolor: `${getStatusColor(station.status)}15`,
                            color: getStatusColor(station.status),
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "11px",
                            borderRadius: "999px",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.black,
                            fontWeight: 400,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "13px",
                          }}
                        >
                          {station.manager}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.textGray,
                            fontWeight: 400,
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "13px",
                          }}
                        >
                          {station.city}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          sx={{ color: "#1976D2" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditStation?.(station);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {station.status === "Active" ? (
                          <IconButton
                            size="small"
                            sx={{ color: "#D32F2F" }}
                            title="Deactivate Station"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStation(station);
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            sx={{ color: "#2E7D32" }}
                            title="Activate Station"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStation(station);
                            }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLoading && stationsData.length > 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                Showing {stationsData.length} of {totalCount} stations
              </Typography>
              <Pagination
                count={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                // color="standard"
                shape="rounded"
              />
            </Box>
          )}

          {!isLoading && stationsData.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" sx={{ color: "#64748B" }}>
                No stations found matching your criteria
              </Typography>
              <Button
                onClick={handleClearFilters}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Paper>

        <StationStatusConfirmDialog
          open={!!selectedStation}
          isActive={selectedStation?.status === "Active"}
          isSubmitting={isStatusUpdating}
          onClose={() => !isStatusUpdating && setSelectedStation(null)}
          onConfirm={handleToggleStationStatus}
        />
      </Container>
    </Box>
  );
};

export default StationManagement;
